// PracticeExamDetail.js
import React, { useEffect, useCallback, useState } from "react";
import { XCircle, Eye, PlayCircle } from "lucide-react";
import VideoPlayerModal from "./VideoPlayerModal";
import { loadMathJax } from "../mathjax";

const PracticeExamDetail = ({ exam, onClose }) => {
  const [expandedSolutionId, setExpandedSolutionId] = useState(null);
  const [currentVideoSolutionUrl, setCurrentVideoSolutionUrl] = useState(null);
  const [mathJaxReady, setMathJaxReady] = useState(false); // State mới để theo dõi MathJax đã sẵn sàng chưa

  const renderMathJax = useCallback(() => {
    if (window.MathJax) {
      if (
        window.MathJax.typesetPromise &&
        typeof window.MathJax.typesetPromise === "function"
      ) {
        window.MathJax.typesetPromise().catch((err) => {
          console.warn("MathJax typeset error:", err);
        });
      } else if (window.MathJax.Hub && window.MathJax.Hub.Queue) {
        window.MathJax.Hub.Queue(["Typeset", window.MathJax.Hub]);
      } else if (window.MathJax.typeset) {
        try {
          window.MathJax.typeset();
        } catch (err) {
          console.warn("MathJax typeset error:", err);
        }
      }
      // Không cần setTimeout đệ quy ở đây nữa nếu bạn dùng Promise ở useEffect ngoài
    }
  }, []);

  // Chờ MathJax tải xong (src/mathjax.js chỉ tải ở trang có công thức) rồi mới hiện đề và vẽ công thức.
  // Mạng lỗi không tải được MathJax thì sau 4 giây vẫn hiện đề (công thức ở dạng chữ).
  useEffect(() => {
    let done = false;
    const show = () => {
      if (done) return;
      done = true;
      setMathJaxReady(true);
      renderMathJax();
    };
    loadMathJax().then(show);
    const timer = setTimeout(show, 4000);
    return () => clearTimeout(timer);
  }, [renderMathJax]);

  useEffect(() => {
    if (exam && mathJaxReady) {
      const timer = setTimeout(() => {
        renderMathJax();
      }, 0);

      return () => clearTimeout(timer);
    }
  }, [exam, renderMathJax, expandedSolutionId, mathJaxReady]);

  if (!exam) {
    return null;
  }

  const handleToggleSolution = (problemId) => {
    setExpandedSolutionId((prevId) =>
      prevId === problemId ? null : problemId
    );
  };

  const handleWatchVideoSolution = (videoUrl) => {
    if (videoUrl) {
      setCurrentVideoSolutionUrl(videoUrl);
    } else {
      alert("Bài giải video hiện chưa có.");
    }
  };

  const currentExamContent = exam || {
    fullTitle: exam.title,
    subject: "Đang cập nhật",
    grade: "Đang cập nhật",
    duration: "Đang cập nhật",
    problems: [
      {
        id: "no-content",
        title: "Nội dung đang cập nhật",
        questions: '<p class="my-2">Nội dung đề thi đang được xây dựng.</p>',
        problemSolutionText: "",
        problemVideoSolutionUrl: "",
      },
    ],
    notes: "Chưa có thông tin chi tiết cho đề thi này.",
  };
  return (
    <div className="bg-white rounded-lg shadow-xl p-6 lg:p-8 w-full relative border border-gray-200 mt-8">
      <button
        onClick={onClose}
        className="absolute top-1 right-1 text-gray-500 hover:text-gray-800"
        aria-label="Close exam details"
      >
        <XCircle size={30} />
      </button>
      <h3 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-4 border-b pb-2 text-center">
        {currentExamContent.title}
      </h3>

      <div className="text-gray-600 italic text-center">
        <p>
          <strong className="font-semibold">Thời gian làm bài:</strong>{" "}
          {currentExamContent.duration}
        </p>
        <p>
          <strong className="font-semibold">Ghi chú:</strong>{" "}
          {currentExamContent.notes}
        </p>
      </div>
      <div className="mb-6 mt-6">
        {mathJaxReady ? (
          currentExamContent.problems.length > 0 ? (
            <div className="space-y-6">
              {currentExamContent.problems.map((problem) => (
                <div key={problem.id} className="problem-section">
                  <div className="flex items-center justify-between">
                    <h5
                      className="text-xl font-bold text-blue-700"
                      dangerouslySetInnerHTML={{ __html: problem.title }}
                    ></h5>
                    <div className="flex space-x-2">
                      {problem.problemSolutionText && (
                        <button
                          onClick={() => handleToggleSolution(problem.id)}
                          className="flex items-center px-2 py-1 bg-indigo-500 text-white rounded-full hover:bg-indigo-600 transition-colors shadow-md text-xs"
                        >
                          <Eye size={14} className="mr-1" />
                          {expandedSolutionId === problem.id ? "Ẩn" : "Giải"}
                        </button>
                      )}
                      {problem.problemVideoSolutionUrl && (
                        <button
                          onClick={() =>
                            handleWatchVideoSolution(
                              problem.problemVideoSolutionUrl
                            )
                          }
                          className="flex items-center px-2 py-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors shadow-md text-xs"
                        >
                          <PlayCircle size={14} className="mr-1" />
                          Video
                        </button>
                      )}
                    </div>
                  </div>

                  {expandedSolutionId === problem.id &&
                    problem.problemSolutionText && (
                      <div className="mt-4 p-4 border border-gray-400 rounded-lg solution-box animate-fade-in">
                        <div
                          dangerouslySetInnerHTML={{
                            __html: problem.problemSolutionText,
                          }}
                        ></div>
                      </div>
                    )}
                  <div className="space-y-4 text-gray-800 mt-4">
                    {problem.questions && (
                      <div className="question-item">
                        <div
                          dangerouslySetInnerHTML={{
                            __html: problem.questions,
                          }}
                        ></div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-600">Nội dung đề thi đang được cập nhật.</p>
          )
        ) : (
          <p className="text-gray-600">Đang tải công thức toán học...</p>
        )}
      </div>

      <div className="mt-6 flex justify-end">
        <button
          onClick={onClose}
          className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-6 rounded-full shadow-md transition-all duration-300"
        >
          Đóng chi tiết
        </button>
      </div>

      <VideoPlayerModal
        videoUrl={currentVideoSolutionUrl}
        onClose={() => setCurrentVideoSolutionUrl(null)}
      />
    </div>
  );
};

export default PracticeExamDetail;
